import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Download, Wand2 } from 'lucide-react';
import { useBackgroundRemoval } from '@/hooks/useBackgroundRemoval';
import { BeforeAfterComparison } from './BeforeAfterComparison';
import type { UploadedImage } from '@/pages/ImageToolsHub';

interface BackgroundRemovalToolProps {
  images: UploadedImage[];
}

export const BackgroundRemovalTool: React.FC<BackgroundRemovalToolProps> = ({ images }) => {
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [useBackgroundColor, setUseBackgroundColor] = useState(false);
  const [customBackground, setCustomBackground] = useState<File | null>(null);
  const [addShadow, setAddShadow] = useState(false);
  const [featherEdges, setFeatherEdges] = useState(0);
  
  const { isProcessing, progress, processedImages, processBatch, clearResults } = useBackgroundRemoval();
  
  const selectedImages = images.filter(img => img.selected);

  const handleProcess = async () => {
    if (selectedImages.length === 0) return;
    
    const files = selectedImages.map(img => img.file);
    await processBatch(files, {
      backgroundColor: useBackgroundColor ? backgroundColor : undefined,
      customBackground,
      addShadow,
      featherEdges,
    });
  };

  const handleDownload = (url: string, name: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `bg-removed-${name}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadAll = () => {
    processedImages.forEach((img, index) => {
      setTimeout(() => {
        handleDownload(img.processed, `image-${index + 1}.png`);
      }, index * 100);
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Background Removal Settings</CardTitle>
          <CardDescription>
            Configure options for background removal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="use-bg-color">Replace with solid color</Label>
              <Switch
                id="use-bg-color"
                checked={useBackgroundColor}
                onCheckedChange={setUseBackgroundColor}
              />
            </div>
            
            {useBackgroundColor && (
              <div className="space-y-2">
                <Label htmlFor="bg-color">Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="bg-color"
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="custom-bg">Custom Background Image</Label>
              <Input
                id="custom-bg"
                type="file"
                accept="image/*"
                onChange={(e) => setCustomBackground(e.target.files?.[0] || null)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="add-shadow">Add shadow effect</Label>
              <Switch
                id="add-shadow"
                checked={addShadow}
                onCheckedChange={setAddShadow}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feather">Feather Edges: {featherEdges}px</Label>
              <Slider
                id="feather"
                min={0}
                max={20}
                step={1}
                value={[featherEdges]}
                onValueChange={(value) => setFeatherEdges(value[0])}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleProcess}
              disabled={selectedImages.length === 0 || isProcessing}
              className="flex-1"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {isProcessing ? 'Processing...' : `Remove Background (${selectedImages.length})`}
            </Button>
            
            {processedImages.length > 0 && (
              <Button onClick={clearResults} variant="outline">
                Clear Results
              </Button>
            )}
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                {Math.round(progress)}% complete
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {processedImages.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Results ({processedImages.length})</CardTitle>
              <Button onClick={handleDownloadAll} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Download All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {processedImages.map((img) => (
                <div key={img.id}>
                  <BeforeAfterComparison
                    beforeImage={img.original}
                    afterImage={img.processed}
                  />
                  <Button
                    onClick={() => handleDownload(img.processed, 'image.png')}
                    variant="outline"
                    size="sm"
                    className="mt-2"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
